import React, {Fragment, useState} from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {Trans} from '@ui/i18n/trans';
import {DeleteSelectedItemsAction} from '@common/datatable/page/delete-selected-items-action';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';
import movieNightImage from './movie-night.svg';
import {DataTableAddItemButton} from '@common/datatable/data-table-add-item-button';
import {Link, useParams} from 'react-router-dom';
import {useTitle} from '@app/titles/requests/use-title';
import {Lien} from '@app/titles/models/lien';
import {ColumnConfig} from '@common/datatable/column-config';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {FormattedNumber} from '@ui/i18n/formatted-number';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {EditIcon} from '@ui/icons/material/Edit';
import {EnumLinkStatusChip} from '@ui/i18n/enum-link-status';
import {EnumLinkQualityChip} from '@ui/i18n/enum-quality';
import {Tooltip} from '@ui/tooltip/tooltip';
import {IconButton} from '@ui/buttons/icon-button';
import {useUpdateLien} from '@app/admin/liens/requests/use-update-link';
import EditableText from '@app/admin/liens/editable-text';
import {queryClient} from '@common/http/query-client';
import {DatatableDataQueryKey} from '@common/datatable/requests/paginated-resources';
import {Title} from '@app/titles/models/title';

function LienColumn({lien}: {lien: Lien}) {
  const [currentLien, setCurrentLien] = useState(lien.lien);
  const updateLien = useUpdateLien();
  return (
    <div className="flex items-center gap-12">
      <div className="min-w-0 overflow-hidden">
        <div className="overflow-hidden overflow-ellipsis">
          <EditableText
            name={`lien[${lien.id}]`}
            value={currentLien}
            onChange={value => {
              updateLien.mutate(
                {lien: value, id: lien.id},
                {
                  onSuccess: data => {
                    queryClient.invalidateQueries({
                      queryKey: DatatableDataQueryKey('liens', {
                        title_id: lien.title_id,
                        loader: 'liens',
                      }),
                    });
                    setCurrentLien(data.data.lien.lien);
                  },
                },
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function LiensListDatatablePageAdmin({
  loader,
  title,
}: {
  loader: string;
  title: Title;
}) {
  const {titleId} = useParams();

  const actions: ColumnConfig<Lien>[] = [
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      hideHeader: true,
      visibleInMode: 'all',
      align: 'end',
      width: 'w-84 flex-shrink-0',
      body: lien => (
        <Fragment>
          <Link
            to={`/admin/liens/${lien.title_id}/${lien.id}/edit`}
            className="text-muted"
          >
            <Tooltip label={<Trans message="Edit" />}>
              <IconButton size="md">
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Link>
        </Fragment>
      ),
    },
  ];
  const commonColumns: ColumnConfig<Lien>[] = [
    {
      key: 'lien',
      width: 'flex-3',
      visibleInMode: 'all',
      header: () => <Trans message="Lien" />,
      body: lien => <LienColumn lien={lien} />,
    },
    {
      key: 'active',
      allowsSorting: true,
      header: () => <Trans message="Status" />,
      body: lien => <EnumLinkStatusChip status={lien.active} />,
    },
    {
      key: 'qualite',
      allowsSorting: true,
      header: () => <Trans message="Quality" />,
      body: lien => <EnumLinkQualityChip quality={lien.qual?.qual} />,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'langues_compact',
      header: () => <Trans message="Languages" />,
      body: lien => (
        <ChipList radius="rounded" size="xs">
          {lien?.langues_compact?.map(lang => (
            <Chip key={lang.id} selectable>
              <Trans message={lang.name} />
            </Chip>
          ))}
        </ChipList>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'subs_compact',
      header: () => <Trans message="Subtitles" />,
      body: lien => (
        <ChipList radius="rounded" size="xs">
          {lien?.subs_compact?.map(lang => (
            <Chip key={lang.id} selectable>
              <Trans message={lang.name} />
            </Chip>
          ))}
        </ChipList>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'reported',
      allowsSorting: true,
      header: () => <Trans message="Reports" />,
      body: lien =>
        lien.reports_count ? <FormattedNumber value={lien.reports_count} /> : 0,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'view',
      allowsSorting: true,
      header: () => <Trans message="Downloads" />,
      body: lien => <FormattedNumber value={lien.view} />,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'updated_at',
      allowsSorting: true,
      width: 'w-124 flex-shrink-0',
      header: () => <Trans message="Last updated" />,
      body: lien =>
        lien.updated_at ? (
          <FormattedDate preset="short" date={lien.updated_at} />
        ) : (
          ''
        ),
    },
  ];

  const LiensListDatatableColumns: ColumnConfig<Lien>[] = [
    ...commonColumns,
    ...actions,
  ];
  const LiensListDatatableColumnsSerie: ColumnConfig<Lien>[] = [
    ...commonColumns,
    {
      key: 'saison',
      allowsSorting: true,
      header: () => <Trans message="Season" />,
      body: lien => <FormattedNumber value={lien.saison} />,
    },
    {
      key: 'episode',
      allowsSorting: true,
      header: () => <Trans message="Episode" />,
      body: lien => <FormattedNumber value={lien.episode} />,
    },
    ...actions,
  ];

  return (
    <DataTablePage
      endpoint={'liens'}
      queryParams={{title_id: titleId, loader: loader}}
      title={<Trans message="Liens" />}
      columns={
        title?.is_series
          ? LiensListDatatableColumnsSerie
          : LiensListDatatableColumns
      }
      actions={<Actions />}
      selectedActions={<DeleteSelectedItemsAction />}
      emptyStateMessage={
        <DataTableEmptyStateMessage
          image={movieNightImage}
          title={<Trans message="No links have been created yet" />}
          filteringTitle={<Trans message="No matching links" />}
        />
      }
    />
  );
}

function Actions() {
  return (
    <Fragment>
      <DataTableAddItemButton elementType={Link} to="new">
        <Trans message="Add Links" />
      </DataTableAddItemButton>
    </Fragment>
  );
}
